import {
  IRouterMiddleware,
  MiddlewareRoute,
  MiddlewareCallback
} from '../middleware.interface';
import { sanitizeHtml } from '@/utils/sanitizeHtml';
import { isExternalOauthSessionOnline, isOauthSession } from '../router-utils';

import store from '@/store';

import AuthorizationService from '@/api/AuthorizationService';
import ManagerService from '@/api/ManagerService';
import { SOCIAL_LOGIN_REDIRECT_KEY } from '@/api/SigninService';
import { app } from '@/main';
import {
  OnboardingURL,
  OnlineOnboardingURL,
  getSignUpUrl
} from '@/views/Onboarding/helpers';
import refreshBusinessInformation from '@/utils/refresh-business-information';

class AuthInstance {
  service = new AuthorizationService();
  managerService = new ManagerService();

  constructor(
    private readonly to: MiddlewareRoute,
    private readonly from: MiddlewareRoute,
    private readonly next: MiddlewareCallback
  ) {}

  async execute() {
    const flags = store.getters['flags/getFlags'] || [];
    const { type, provider } = this.to.params;
    const { error, token } = this.to.query;

    const enableInviteRedirect = new Set(['google', 'apple']);

    const socialLoginRedirectData = localStorage.getItem(
      SOCIAL_LOGIN_REDIRECT_KEY
    );
    let defaultPath = '/';
    let parsedInvitationData: Record<string, string> = {};

    if (enableInviteRedirect.has(provider) && socialLoginRedirectData) {
      parsedInvitationData = JSON.parse(socialLoginRedirectData);

      if (parsedInvitationData.redirect) {
        defaultPath = parsedInvitationData.redirect;
      }
    }

    if (error) {
      const redirect = await this.getErrorRedirect(
        type,
        token,
        socialLoginRedirectData ? JSON.parse(socialLoginRedirectData) : {}
      );
      const safeError = sanitizeHtml(String(error));
      app.$notification(safeError, 'error');
      this.next(`${redirect.error}`);
    } else if (token && typeof token === 'string') {
      try {
        await this.service.exchangeUrlTokenForCookie(token);
      } catch {
        this.next('/');
        return;
      }

      if (parsedInvitationData.inviteCode) {
        const { data } = await this.managerService.acceptInvite(
          parsedInvitationData.inviteCode
        );
        this.service.setCredentials(data.access_token, false);
        await refreshBusinessInformation();
        localStorage.removeItem(SOCIAL_LOGIN_REDIRECT_KEY);
      }

      if (isOauthSession()) {
        if (isExternalOauthSessionOnline() && type !== 'signin')
          this.next(OnlineOnboardingURL.REWARD_RATIO);
        else this.next({ name: 'merchant-connect-oauth' });
      } else if (type !== 'signin' && defaultPath === '/') {
        this.next(OnboardingURL.WELCOME_TWISM);
      } else {
        window.location.href = defaultPath;
      }
    } else {
      this.next(defaultPath);
    }
  }

  async getErrorRedirect(
    type: string,
    token: string | (string | null)[],
    sessionData: Record<string, string>
  ) {
    if (type !== 'signin' || (isOauthSession() && token && token.length)) {
      if (sessionData && sessionData.referrerURL) {
        return { error: sessionData.referrerURL };
      }

      return {
        error: getSignUpUrl()
      };
    }

    return {
      error: '/'
    };
  }
}

export class HandleAuthCallback implements IRouterMiddleware {
  private routes = new Set(['auth-callback']);

  canActivate(to: MiddlewareRoute, from: MiddlewareRoute): boolean {
    return to.name ? this.routes.has(to.name) : false;
  }

  async execute(
    to: MiddlewareRoute,
    from: MiddlewareRoute,
    next: MiddlewareCallback
  ): Promise<void> {
    const instance = new AuthInstance(to, from, next);
    await instance.execute();

    localStorage.removeItem(SOCIAL_LOGIN_REDIRECT_KEY);
  }
}
